/**
 * Pure rules of the Billing module navigation (reorganisation of 2026-10-28).
 *
 * The former Billing dashboard held three tabs (New billing requests / Billing tracking / Invoice
 * recovery) and the Upcoming invoices page two (Upcoming invoices / Terminated deals fees). Each of
 * them is now a page of its own, rendered by the same component with a `section` taken from the
 * route data. These helpers turn that route data into the section, so an unknown or legacy value
 * always lands on a page that exists.
 */

/** Routes of the Billing pages, shared by the sidebar, the redirects and the Overview links. */
export const BILLING_PAGE_ROUTES = {
  overview: '/billing/overview',
  requests: '/billing/billing-requests',
  timeManagement: '/billing/dashboard-billing-time-management',
  pennylane: '/billing/pennylane',
  tracking: '/billing/pennylane/billing-tracking',
  subscriptions: '/billing/pennylane/subscriptions',
  recovery: '/billing/pennylane/recovery',
  kpi: '/billing/kpi',
  settings: '/billing/billing-settings',
} as const;

/**
 * Roles that open the Billing KPI page (terminated deals fees report), decided on 2026-10-30.
 * Normalised slugs (lower case, underscores as hyphens): `billing_manager` matches.
 * Mirror of Billing_kpi_access_rules::ALLOWED_GROUPS on the backend: change both together.
 */
export const BILLING_KPI_ROLES = ['superadmin', 'billing', 'billing-manager', 'billing-kpi'] as const;

function normalizeBillingRole(role: unknown): string {
  return String(role ?? '').trim().toLowerCase().replace(/_/g, '-');
}

function userGroups(groups: unknown): string[] {
  return Array.isArray(groups) ? groups.map(normalizeBillingRole) : [];
}

/** True when one of the user's groups opens the Billing KPI page. */
export function canAccessBillingKpi(groups: unknown): boolean {
  return userGroups(groups).some((role) => (BILLING_KPI_ROLES as readonly string[]).includes(role));
}

/** Path of a router URL without its query string nor fragment. */
function billingUrlPath(url: unknown): string {
  return String(url ?? '').split(/[?#]/)[0].replace(/\/+$/, '');
}

/** True for the Billing KPI page, and for its former /billing/insights URL that redirects to it. */
export function isBillingKpiUrl(url: unknown): boolean {
  const path = billingUrlPath(url);
  return path === '/billing/kpi' || path === '/billing/insights';
}

/**
 * Whether the user may open a /billing URL. The KPI page has its own roles; every other
 * Billing page stays reserved to billing and superadmin, and Time management also opens to
 * timemanager.
 */
export function canAccessBillingUrl(url: unknown, groups: unknown): boolean {
  if (isBillingKpiUrl(url)) {
    return canAccessBillingKpi(groups);
  }
  const roles = userGroups(groups);
  if (roles.includes('billing') || roles.includes('superadmin')) {
    return true;
  }
  return roles.includes('timemanager') && billingUrlPath(url).includes('dashboard-billing-time-management');
}

export type BillingDashboardSection = 'requests' | 'tracking' | 'recovery';

const BILLING_DASHBOARD_SECTIONS: readonly BillingDashboardSection[] = ['requests', 'tracking', 'recovery'];

/** Section of the billing-dashboard component for a route data value; 'requests' when unknown. */
export function billingDashboardSection(value: unknown): BillingDashboardSection {
  const section = String(value ?? '').trim() as BillingDashboardSection;
  return BILLING_DASHBOARD_SECTIONS.includes(section) ? section : 'requests';
}

/**
 * Former tab index of a section: the component's loaders still key on it
 * (0 = requests, 1 = tracking, 2 = recovery).
 */
export function billingDashboardTabIndex(section: BillingDashboardSection): number {
  const index = BILLING_DASHBOARD_SECTIONS.indexOf(section);
  return index === -1 ? 0 : index;
}

export type UpcomingInvoicesSection = 'subscriptions' | 'upcoming' | 'terminated';

/**
 * Section of the upcoming-invoices component: 'subscriptions' (Pennylane subscriptions),
 * 'upcoming' (AP subscriptions: the upcoming invoices computed from the deals) or 'terminated'
 * (KPI: fees lost by terminated deals). The legacy 'invoices' value and anything unknown
 * give 'upcoming'.
 */
export function upcomingInvoicesSection(value: unknown): UpcomingInvoicesSection {
  const section = String(value ?? '').trim();
  if (section === 'subscriptions' || section === 'terminated') {
    return section;
  }
  return 'upcoming';
}

export type BillingSubscriptionsTab = 'pennylane' | 'ap';

/** Tab of Pennylane › Subscriptions from the `tab` query parameter; 'pennylane' by default. */
export function billingSubscriptionsTab(value: unknown): BillingSubscriptionsTab {
  return String(value ?? '').trim().toLowerCase() === 'ap' ? 'ap' : 'pennylane';
}

/** Index of a Subscriptions tab: Pennylane subscriptions first, AP subscriptions second. */
export function billingSubscriptionsTabIndex(tab: BillingSubscriptionsTab): number {
  return tab === 'ap' ? 1 : 0;
}
